import { z } from "zod";
import { isAllowedContentWord } from "../domain/normalization";
import type { LevelDefinition } from "../domain/types";

export const VISUAL_CUES = new Set([
  "family_shield",
  "protection_pulse",
  "heart_restore",
  "sober_fade",
  "goal_pulse",
  "family_highlight",
  "goal_particles",
  "vehicle_shield",
  "medical_heart",
  "tow_truck",
  "assist_aura",
  "calendar_check",
  "future_light",
  "income_flow",
  "protected_family",
  "capital_restore",
  "double_points",
  "planning_shield",
  "recovery_heart",
]);

const rewardSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("shield") }).strict(),
  z.object({ type: z.literal("heal"), amount: z.literal(1) }).strict(),
  z
    .object({ type: z.literal("bonus_points"), amount: z.literal(500) })
    .strict(),
  z.object({ type: z.literal("combo_boost"), amount: z.literal(2) }).strict(),
  z.object({ type: z.literal("none") }).strict(),
]);

const eventSchema = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9_]*$/u),
    words: z
      .array(
        z
          .string()
          .refine(
            isAllowedContentWord,
            "Use only uppercase Spanish letters and spaces",
          ),
      )
      .min(1),
    type: z.enum(["threat", "opportunity", "recovery"]),
    basePoints: z.literal(100),
    response: z
      .object({
        product: z.string().min(1),
        message: z.string().min(1),
        visualCue: z
          .string()
          .refine((value) => VISUAL_CUES.has(value), "Unknown visual cue"),
      })
      .strict(),
    reward: rewardSchema,
  })
  .strict();

export const levelSchema = z
  .object({
    schemaVersion: z.literal(1),
    id: z.enum(["vida", "soat", "rentas"]),
    universe: z.string().min(1),
    title: z.string().min(1),
    objective: z.string().min(1),
    artTheme: z.enum(["vida", "soat", "rentas"]),
    approvalStatus: z.enum(["prototype", "approved"]),
    cta: z
      .object({
        label: z.literal("CONOCER MÁS"),
        url: z.url().refine((value) => {
          const url = new URL(value);
          return (
            url.protocol === "https:" &&
            (url.hostname === "interseguro.pe" ||
              url.hostname.endsWith(".interseguro.pe"))
          );
        }, "CTA must use HTTPS on interseguro.pe"),
      })
      .strict(),
    events: z.array(eventSchema).min(1),
    sequence: z.array(z.string()).length(20),
    recoveryEventId: z.string().regex(/^[a-z][a-z0-9_]*$/u),
  })
  .strict()
  .superRefine((level, context) => {
    if (level.id !== level.artTheme) {
      context.addIssue({
        code: "custom",
        message: "artTheme must match id",
        path: ["artTheme"],
      });
    }
    const ids = level.events.map(({ id }) => id);
    if (new Set(ids).size !== ids.length) {
      context.addIssue({
        code: "custom",
        message: "Event IDs must be unique",
        path: ["events"],
      });
    }
    level.sequence.forEach((id, index) => {
      if (!ids.includes(id)) {
        context.addIssue({
          code: "custom",
          message: `Unknown sequence event ${id}`,
          path: ["sequence", index],
        });
      }
    });
    const recovery = level.events.find(
      ({ id }) => id === level.recoveryEventId,
    );
    if (!recovery || recovery.type !== "recovery") {
      context.addIssue({
        code: "custom",
        message: "recoveryEventId must reference a recovery event",
        path: ["recoveryEventId"],
      });
    }
    if (level.sequence.includes(level.recoveryEventId)) {
      context.addIssue({
        code: "custom",
        message: "Recovery cannot appear in the base sequence",
        path: ["sequence"],
      });
    }
    level.events.forEach((event, eventIndex) => {
      const normalizedWords = event.words.map((word) =>
        word.normalize("NFC").toLocaleUpperCase("es-PE"),
      );
      if (new Set(normalizedWords).size !== normalizedWords.length) {
        context.addIssue({
          code: "custom",
          message: "Event variants must be unique",
          path: ["events", eventIndex, "words"],
        });
      }
      const isHeal = event.reward.type === "heal";
      if ((event.type === "recovery") !== isHeal) {
        context.addIssue({
          code: "custom",
          message: "Only recovery events may heal, and recovery must heal",
          path: ["events", eventIndex, "reward"],
        });
      }
    });
  });

export function parseLevel(value: unknown): LevelDefinition {
  return levelSchema.parse(value) as LevelDefinition;
}

const operationalSchema = z.object({ ctaEnabled: z.boolean() }).strict();
export type OperationalConfig = z.infer<typeof operationalSchema>;
export const parseOperationalConfig = (value: unknown): OperationalConfig =>
  operationalSchema.parse(value);
